import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { filterValuesSeparator } from 'shared-helpers/consts';
import api from 'api';
import { actions } from 'store';
import { useApiData } from 'lib/api';
import {
  ContainerWithShowAll,
  ModalButtonWithForm,
  Owners,
  TitleWithIcon
} from 'components/common';
import { FormGroup, Text } from 'components/common/form';
import { useBoxContext } from 'components/common/box';

import type { IndividualAchievement } from 'shared-types';
import type { GetIndividualAchievementsRes } from 'shared-types/responses';

interface Props {
  individualAchievement?: Pick<
    IndividualAchievement,
    'id' | 'title' | 'UserId'
  >;
  isRequest?: boolean;
  updateData: () => Promise<void>;
}

const AchievementsIndividualDelete = ({
  individualAchievement,
  isRequest = false,
  updateData
}: Props) => {
  const { dispatch } = useDispatch();

  const { selectedItems } = useBoxContext();
  const selectedIds = Object.keys(selectedItems).map((key) => Number(key));

  const {
    messages,
    serverConfig: { requireReasonWhenDeletingData },
    user
  } = useReduxState();

  const [fetch, setFetch] = useState(false);

  const { data, loading } = useApiData<GetIndividualAchievementsRes>({
    url: `/admin/achievements-individual?ids=${
      individualAchievement?.id || selectedIds.join(filterValuesSeparator)
    }`,
    onlyIf: Boolean(user?.id) && fetch
  });

  const handleSubmit = async (values?: { deletionReason?: string }) => {
    try {
      await api.post(`/admin/achievements-individual/delete`, {
        individualAchievementIds: individualAchievement
          ? [individualAchievement.id]
          : selectedIds,
        ...(values?.deletionReason && { deletionReason: values.deletionReason })
      });
      await updateData();

      dispatch(
        actions.addNotice({
          text: isRequest
            ? messages.profileDeleteCancelSuccess
            : messages.achievementDeleteSuccess,
          type: 'success'
        })
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <ModalButtonWithForm
      buttonCategory="danger"
      buttonIcon={
        isRequest &&
        individualAchievement &&
        user?.id === individualAchievement?.UserId
          ? 'ban'
          : 'trash-alt'
      }
      buttonIsDropdown={isRequest || !Boolean(individualAchievement)}
      buttonSize="header"
      buttonText={
        isRequest
          ? messages.profileDeleteCancel
          : individualAchievement
          ? messages.achievementDelete
          : messages.achievementsIndividualDeleteAllSelected
      }
      buttonDisabled={
        !individualAchievement && Object.keys(selectedItems).length < 1
      }
      initialValues={{}}
      modalActionText={
        isRequest ? messages.commonConfirm : messages.commonDelete
      }
      modalTitle={
        isRequest
          ? messages.profileDeleteCancel
          : individualAchievement
          ? messages.achievementsIndividualDelete
          : messages.achievementsIndividualDeleteIndividualAchievements
      }
      modalWidth={Boolean(individualAchievement) ? 'small' : 'wide'}
      onShow={() => setFetch(true)}
      onHide={() => setFetch(false)}
      onSubmit={handleSubmit}
    >
      {() => (
        <div>
          {isRequest ? null : (
            <ContainerWithShowAll
              baseMaxHeight={45}
              items={data?.individualAchievements.map((achievement) => (
                <TitleWithIcon
                  key={achievement.id}
                  icon="star"
                  title={achievement.title}
                >
                  <Owners owners={[achievement.User]} />
                </TitleWithIcon>
              ))}
              loading={loading}
              style={{ marginTop: 10 }}
            />
          )}

          <div style={{ marginBottom: requireReasonWhenDeletingData ? 20 : 0 }}>
            {isRequest
              ? messages.accessRequestCancelConfirm
              : individualAchievement
              ? messages.achievementDeleteConfirm
              : messages.achievementsIndividualAreYouSureYouWantToDeleteThese}
          </div>

          {requireReasonWhenDeletingData && !isRequest ? (
            <FormGroup
              htmlFor={`deletionReason-individual-${
                individualAchievement?.id || 'bulk'
              }`}
              label={messages.commonDeletionReason}
            >
              <Text
                field="deletionReason"
                id={`deletionReason-individual-${
                  individualAchievement?.id || 'bulk'
                }`}
                maxLength={255}
                autocomplete="off"
                placeholder={messages.commonInputDeletionReason}
                required={true}
              />
            </FormGroup>
          ) : null}
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementsIndividualDelete;
